import React, {useState, useCallback} from 'react';
import {
    DatePicker,
    Banner,
    Button,
    TextStyle,
    Filters,
    Card,
    Tabs,
    ChoiceList,
    Autocomplete,
    TextField,
    Modal,
    Stack,
    TextContainer,
    IndexTable,
    Frame,
    Loading,
    DataTable,
    Page
} from '@shopify/polaris';
import HttpClient from './appHttpRequests';
import moment from 'moment';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import SettingsPage from './settingsPage';
import {generateExtraParams, generateQueryParam} from "./helper";

function OrderedProductsListView() {

    const [selectedDate, setSelectedDate] = useState({start: new Date(), end: new Date()});
    const [customFilterValues, setCustomFilterValues] = useState({});
    const [queryValue, setQueryValue] = useState('');
    const [showTable, setShowTable] = useState('products');

    const [productTableRows, setProductTableRows] = useState([]);
    const [orders, setOrders] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [allOrdersSelected, setAllOrdersSelected] = useState(false);
    const [isDataLoading, setDataLoading] = useState(false);
    const [isExcelLoading, setExcelLoading] = useState(false);
    const [loadingError, setLoadingError] = useState('');
    const [excelLoadingError, setExcelLoadingError] = useState('');

    const [isSettingsDialogOpen, setSettingsDialogOpen] = useState(false);

    const [showTagOrdersModal, setShowTagOrdersModal] = useState(false);
    const [newTagForSelectedOrders, setNewTagForSelectedOrders] = useState('');
    const [isOrderTaggingInProgress, setOrderTaggingInProgress] = useState(false);
    const [orderTaggingError, setOrderTaggingError] = useState('');

    const handleMonthChange = useCallback(
        (month, year) => {
            setSelectedDate({
                start: new Date(year, month, moment(selectedDate.start).day()),
                end: new Date(year, month, moment(selectedDate.start).day())
            })
        },
        [selectedDate.start]
    );

    const handleExcelDownload = async () => {
        setExcelLoading(true);
        let q_params = {
            date: moment(selectedDate.start).format('YYYY-MM-DD'),
            query: generateQueryParam(queryValue, customFilterValues),
            view: showTable,
            format: 'excel',
            ...generateExtraParams(customFilterValues)
        }
        HttpClient.get(window.SHOPIFY_APP_API_URL + '/ordered-products', {
            responseType: 'blob',
            params: q_params
        })
            .then((response) => {
                if (response.status === 200) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'export-' + moment(selectedDate.start).format('DD/MM/YYYY') + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                } else {
                    setExcelLoadingError(response.statusText);
                }
                setExcelLoading(false);
            })
            .catch((error) => {
                setExcelLoading(false);
                setExcelLoadingError(error.response && error.response.data && error.response.data.length < 64
                    ? error.response.data : (error.response && error.response.statusText ? error.response.statusText : 'Unknown error'));
//                console.log(error)
            });
    }

    const handleDPDDownload = async () => {
        setExcelLoading(true);
        let q_params = {
            date: moment(selectedDate.start).format('YYYY-MM-DD'),
            query: generateQueryParam(queryValue, customFilterValues),
            view: showTable,
            format: 'csv',
            ...generateExtraParams(customFilterValues)
        }
        HttpClient.get(window.SHOPIFY_APP_API_URL + '/ordered-products', {
            responseType: 'blob',
            params: q_params
        })
            .then((response) => {
                if (response.status === 200) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'export-' + moment(selectedDate.start).format('DD/MM/YYYY') + '.csv');
                    document.body.appendChild(link);
                    link.click();
                } else {
                    setExcelLoadingError(response.statusText);
                }
                setExcelLoading(false);
            })
            .catch((error) => {
                setExcelLoading(false);
                setExcelLoadingError(error.response && error.response.data && error.response.data.length < 64
                    ? error.response.data : (error.response && error.response.statusText ? error.response.statusText : 'Unknown error'));
//                console.log(error)
            });
    }

    const handleFiltersApply = useCallback(() => {
        setDataLoading(true);
        setProductTableRows([]);
        setOrders([]);
        let q_params = {
            date: moment(selectedDate.start).format('YYYY-MM-DD'),
            query: generateQueryParam(queryValue, customFilterValues),
            ...generateExtraParams(customFilterValues)
        }
        //console.log(q_params);
        HttpClient.get(window.SHOPIFY_APP_API_URL + '/ordered-products', {
            params: q_params
        })
            .then((response) => {
                //console.log(response);
                if (response.status === 200) {
                    let ordered_products = response.data.products;
                    let orders = response.data.orders;
                    let product_rows = [];
                    for (let sku in ordered_products) {
                        let product = ordered_products[sku];
                        let product_name = product.name;
                        if (product.confused)
                            product_name = <TextStyle variation="negative">{product_name}</TextStyle>
                        let orders_with_comments = [];
                        for (let order_name in product.orders) {
                            let order = orders.find(o => o.name === order_name);
                            if (order && order.note && order.note.length > 0) {
                                orders_with_comments.push({
                                    name: order.name,
                                    note: order.note
                                });
                            }
                        }
                        product_rows.push([
                            sku,
                            product_name,
                            product.whole_portions,
                            product.qty.toFixed(1),
                            (
                                <React.Fragment key="1">
                                    {orders_with_comments.map(order =>
                                        <div key={order.name}><TextStyle variation="strong">#{order.name}</TextStyle>: {order.note}</div>
                                    )}
                                </React.Fragment>
                            )

                        ]);
                    }
                    setProductTableRows(product_rows);
                    setOrders(orders);
                    setLoadingError('');
                } else {
                    setProductTableRows([]);
                    setOrders([]);
                    setLoadingError(response.statusText);
                }
                setDataLoading(false);
            })
            .catch((error) => {
                setProductTableRows([]);
                setOrders([]);
                setDataLoading(false);
                setLoadingError(error.response && error.response.data && error.response.data.length < 64
                    ? error.response.data : (error.response && error.response.statusText ? error.response.statusText : 'Unknown error'));
            });
    }, [customFilterValues, queryValue, selectedDate]);

    const handleOrderDateChange = useCallback(
        (value) => setSelectedDate(value),
        [],
    );
    const handleFiltersQueryChange = useCallback(
        (value) => setQueryValue(value),
        [],
    );

    const handleOrderSelectionChange = useCallback(
        (sel_type, is_selected, order_id) => {
            let new_selectedOrders = selectedOrders.slice()
            if (sel_type === 'single') {
                if (is_selected)
                    new_selectedOrders.push(order_id);
                else
                    new_selectedOrders.splice(new_selectedOrders.indexOf(order_id), 1)
            } else if (sel_type === 'page') {
                if (is_selected) {
                    new_selectedOrders = orders.map((order) => order.name);
                } else {
                    new_selectedOrders = [];
                }
            } else if (sel_type === 'multi') {
                for (let i = order_id[0]; i <= order_id[1]; i++) {
                    if (is_selected) {
                        if (!new_selectedOrders.includes(orders[i].name))
                            new_selectedOrders.push(orders[i].name);
                    } else {
                        if (new_selectedOrders.includes(orders[i].name))
                            new_selectedOrders.splice(new_selectedOrders.indexOf(orders[i].name), 1);
                    }
                }
            }
            setSelectedOrders(new_selectedOrders);
            if (allOrdersSelected !== (new_selectedOrders.length === orders.length))
                setAllOrdersSelected(new_selectedOrders.length === orders.length);
        },
        [selectedOrders, allOrdersSelected, orders]
    );

    const tagSelectedOrders = useCallback(
        () => {
            setOrderTaggingInProgress(true);
            let order_id_list = [];
            for (let i = 0; i < selectedOrders.length; i++) {
                order_id_list.push(orders.find(order => order.name === selectedOrders[i]).id);
            }
            HttpClient.post(window.SHOPIFY_APP_API_URL + '/tag-orders', {
                orders: order_id_list,
                tag: newTagForSelectedOrders
            })
                .then((response) => {
                    if (response.status === 200) {
                        setShowTagOrdersModal(false);
                    } else {
                        setOrderTaggingError(response.statusText);
                    }
                    setOrderTaggingInProgress(false);
                })
                .catch((error) => {
                    setOrderTaggingInProgress(false);
                    setOrderTaggingError(error.response && error.response.data && error.response.data.length < 64
                        ? error.response.data : (error.response && error.response.statusText ? error.response.statusText : 'Unknown error'));
                });
        },
        [selectedOrders, newTagForSelectedOrders, orders]
    );

    const handleCustomFiltersRemove = useCallback((key) => {
        let new_values = {...customFilterValues};
        delete new_values[key];
        setCustomFilterValues(new_values);
    }, [customFilterValues]);
    const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);
    const handleFiltersClearAll = useCallback(() => {
        setCustomFilterValues({});
        handleQueryValueRemove();
    }, [
        handleQueryValueRemove
    ]);

    const filters = [
        {
            key: 'orderDate',
            label: 'Order Date',
            filter: (
                <DatePicker
                    month={moment(selectedDate.start).month()}
                    year={moment(selectedDate.start).year()}
                    onChange={handleOrderDateChange}
                    onMonthChange={handleMonthChange}
                    selected={selectedDate}
                />
            ),
            shortcut: true,
        }
    ];
    if (Array.isArray(window.TENANT_SETTINGS.filters)) {
        for (let filter_index = 0; filter_index < window.TENANT_SETTINGS.filters.length; filter_index++) {
            let filter = window.TENANT_SETTINGS.filters[filter_index];
            let filter_item = {
                key: filter.name,
                label: filter.title,
                shortcut: true,
            };
            if (filter.type === 'single-choice' || filter.type === 'multiple-choice') {
                filter_item.filter = (
                    <ChoiceList
                        title={filter.title}
                        titleHidden
                        allowMultiple={filter.type === 'multiple-choice'}
                        choices={
                            filter.choices.map(choice => {
                                return {label: choice.title, value: choice.name};
                            })}
                        selected={customFilterValues[filter.name] || []}
                        onChange={(value) => {
                            if (filter.type === 'multiple-choice')
                                console.log(value);
                            let new_values = {...customFilterValues};
                            if (filter.type === 'multiple-choice')
                                new_values[filter.name] = value;
                            else
                                new_values[filter.name] = value && value.length > 0 ? value[0] : undefined;
                            setCustomFilterValues(new_values);
                        }}
                    />
                );
            }
            else if (filter.type === 'text-autocomplete') {
                filter_item.filter = (
                <Autocomplete
                    options={filter.choices.map(choice => {
                        return {label: choice, value: choice};
                    })}
                    selected={customFilterValues[filter.name] && customFilterValues[filter.name].length > 0
                         ? [{label: customFilterValues[filter.name], value: customFilterValues[filter.name]}] : []}
                    onSelect={(selected) => {
                        if (Array.isArray(selected) && selected.length > 0)
                        {
                            let new_values = {...customFilterValues};
                            new_values[filter.name] = selected[0] && selected[0].length > 0 ? selected[0] : undefined;
                            setCustomFilterValues(new_values);

                        }
                    }}
                    textField={(
                        <Autocomplete.TextField
                            onChange={(value) => {
                                let new_values = {...customFilterValues};
                                new_values[filter.name] = value && value.length > 0 ? value : undefined;
                                setCustomFilterValues(new_values);
                            }}
                            label={filter.title}
                            labelHidden
                            value={customFilterValues[filter.name]}
                            placeholder={filter.title}
                        />
                    )}
                />
                );
            }
            filters.push(filter_item);
        }
    }

    const appliedFilters = [];
    {
        const key = 'orderDate';
        appliedFilters.push({
            key,
            label: disambiguateLabel(key, selectedDate)
        });
    }
    for (let filter_name in customFilterValues) {
        if (customFilterValues[filter_name]) {
            appliedFilters.push({
                key: filter_name,
                label: disambiguateLabel(filter_name, customFilterValues[filter_name]),
                onRemove: handleCustomFiltersRemove
            });
        }
    }

    if (isSettingsDialogOpen)
    {
        return (
            <SettingsPage
                onClose={() => setSettingsDialogOpen(false)}
            />
        );
    }

    return (
        <Page title="Compound Products" fullWidth
              primaryAction={{
                  content: 'Settings...',
                  onAction: () => setSettingsDialogOpen(true)
              }}
        >
            <Card>
                <Card.Section>
                    <Filters
                        queryValue={queryValue}
                        filters={filters}
                        appliedFilters={appliedFilters}
                        onQueryChange={handleFiltersQueryChange}
                        onQueryClear={handleQueryValueRemove}
                        onClearAll={handleFiltersClearAll}
                        disabled={isDataLoading}
                    >
                        <div style={{paddingLeft: '8px'}}>
                            <Button primary onClick={handleFiltersApply} disabled={isDataLoading}
                                    loading={isDataLoading}>
                                Apply
                            </Button>
                        </div>
                    </Filters>
                </Card.Section>
                {loadingError &&
                <Banner
                    title="Error"
                    status="critical"
                >
                    <p>
                        Unable to load orders: {loadingError}
                    </p>
                </Banner>
                }
                {isDataLoading &&
                <Frame>
                    <Loading/>
                </Frame>
                }
                <Tabs selected={showTable === 'products' ? 0 : 1} tabs={[
                    {
                        id: 'products',
                        content: 'Products View',
                        accessibilityLabel: 'Products View',
                        panelID: 'products-view'
                    },
                    {id: 'orders', content: 'Orders View', accessibilityLabel: 'Orders View', panelID: 'orders-view'}
                ]} onSelect={() => setShowTable(showTable !== 'orders' ? 'orders' : 'products')}>
                    {!isDataLoading && !loadingError && showTable === 'products' &&
                    <DataTable
                        columnContentTypes={[
                            'text',
                            'text',
                            'numeric',
                            'numeric',
                            'text'
                        ]}
                        headings={[
                            'SKU',
                            'Product name',
                            'Whole portions',
                            'Total Qty',
                            'Comments'
                        ]}
                        verticalAlign="top"
                        rows={productTableRows}
                        footerContent={`${orders.length} orders found`}
                    />
                    }
                    {!isDataLoading && !loadingError && showTable === 'orders' &&
                    <IndexTable
                        resourceName={{
                            singular: 'order',
                            plural: 'orders',
                        }}
                        itemCount={orders.length}
                        selectedItemsCount={
                            allOrdersSelected ? 'All' : (selectedOrders ? selectedOrders.length : 0)
                        }
                        onSelectionChange={handleOrderSelectionChange}
                        promotedBulkActions={[{
                            content: 'Tag orders',
                            onAction: () => {
                                let new_tag = moment(selectedDate.start).format('DD/MM/YYYY');
                                for (let filter_index = 0; filter_index < window.TENANT_SETTINGS.filters.length; filter_index++) {
                                    let filter = window.TENANT_SETTINGS.filters[filter_index];
                                    if (customFilterValues[filter.name])
                                        new_tag += '-' + customFilterValues[filter.name].replaceAll(' ', '');
                                }
                                setNewTagForSelectedOrders(new_tag);
                                setShowTagOrdersModal(true);
                            },
                        }]}
                        headings={[
                            {title: 'ID'},
                            {title: 'Email'},
                            {title: 'Name'},
                            {title: 'Address'},
                            {title: 'City'},
                            {title: 'ZIP'},
                            {title: 'Phone'},
                            {title: 'Comments'}
                        ]}
                    >
                        {orders.map(
                            (order, index) => (
                                <IndexTable.Row
                                    id={order.name}
                                    key={order.name}
                                    selected={selectedOrders ? selectedOrders.includes(order.name) : false}
                                    position={index}
                                >
                                    <IndexTable.Cell>
                                        <TextStyle variation="strong">{order.name}</TextStyle>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>{order.email}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.customer_name}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.address}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.city}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.zip}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.phone}</IndexTable.Cell>
                                    <IndexTable.Cell>{order.note}</IndexTable.Cell>
                                </IndexTable.Row>
                            ),
                        )}
                    </IndexTable>
                    }
                </Tabs>
                {showTagOrdersModal &&
                <Modal
                    open
                    onClose={() => setShowTagOrdersModal(false)}
                    title="Tag Orders"
                    primaryAction={{
                        content: 'Tag Orders',
                        loading: isOrderTaggingInProgress,
                        disabled: isOrderTaggingInProgress,
                        onAction: tagSelectedOrders,
                    }}
                    secondaryActions={[
                        {
                            content: 'Cancel',
                            onAction: () => setShowTagOrdersModal(false),
                        }
                    ]}
                >
                    <Modal.Section>
                        <Stack vertical>
                            <Stack.Item>
                                <TextContainer>
                                    <p>
                                        You can tag selected orders with a new tag.
                                        This will allow you to easily select them in other apps and on Shopify Orders
                                        page.
                                    </p>
                                </TextContainer>
                                {orderTaggingError &&
                                <Banner
                                    title="Error"
                                    status="critical"
                                >
                                    <p>
                                        Unable to tag orders: {orderTaggingError}
                                    </p>
                                </Banner>
                                }
                            </Stack.Item>
                            <Stack.Item fill>
                                <TextField
                                    label="Tag"
                                    maxLength={40}
                                    value={newTagForSelectedOrders}
                                    onChange={setNewTagForSelectedOrders}
                                    connectedRight={
                                        <CopyToClipboard text={newTagForSelectedOrders}>
                                            <Button>
                                                Copy tag
                                            </Button>
                                        </CopyToClipboard>
                                    }
                                />
                            </Stack.Item>
                        </Stack>
                    </Modal.Section>
                </Modal>
                }
                <div>
                    <Button primary onClick={handleExcelDownload} disabled={isExcelLoading} loading={isExcelLoading}>Export
                        to Excel</Button>
                    <span className="mr-2">&nbsp;</span>
                    <Button primary onClick={handleDPDDownload} disabled={isExcelLoading} loading={isExcelLoading}>Export
                        to DPD CSV</Button>
                </div>
                {excelLoadingError &&
                <Banner
                    title="Error"
                    status="critical"
                >
                    <p>
                        Unable to export to Excel: {excelLoadingError}
                    </p>
                </Banner>
                }
            </Card>
        </Page>
    );

    function disambiguateLabel(key, value) {
        switch (key) {
            case 'orderDate':
                return `Orders for: ${moment(value.start).format('ddd Do MMM')}`;
//            case 'DriverName':
//                return `Driver: ${value}`;
            default:
            {
                let filter = window.TENANT_SETTINGS.filters.find(f => f.name === key);
                let s = filter.title + ': ';
                if (filter.type === 'single-choice')
                    s += filter.choices.find(c => c.name === value).name;
                else
                    s += value;
                return s;
            }
        }
    }

    /*
        function isEmpty(value) {
            if (Array.isArray(value)) {
                return value.length === 0;
            } else {
                return value === '' || value == null;
            }
        }
     */
}

export default function App() {
    return (
        <OrderedProductsListView />
    );
}

